/**
 * Which side a signed-in person is acting for (client app plan v2.2/v2.3 §B6).
 *
 * A profile belongs to at most one client company (`customer_id`) and one trade company
 * (`trade_company_id`). A dual account has both and acts for one at a time: the side named by
 * `profiles.last_active_role` (the website's and the app's "Viewing as" switch), else its primary
 * account type, else the client side. `sync_user_session` applies the same rule when it decides
 * which side's permissions the session carries (G30), so the screens and the server agree.
 *
 * Consumers switch on `side`; there are no `isClient` flags in new code, so a third side (end
 * customers, G-A) is one more union member plus screens.
 */

export type Side = 'client' | 'trade';

/** The values `notifications.role_context` and `profiles.last_active_role` use. */
export type SideRoleContext = 'customer' | 'trade_company';

export interface ActiveSideProfile {
  customer_id?: string | null;
  trade_company_id?: string | null;
  last_active_role?: string | null;
  primary_account_type?: string | null;
}

export interface ActiveSide {
  /** Null when the profile has no company (a platform admin, or a member who was removed). */
  side: Side | null;
  /** The active side's company: `customer_id` for the client side, `trade_company_id` for trade. */
  companyId: string | null;
  isDual: boolean;
  /** The sides this profile can act for, client first. */
  availableSides: Side[];
}

/**
 * The side a stored role context names. Tolerant: anything unknown (`both`, `platform`, null, a
 * future value) is null, meaning "not tied to one side".
 */
export function sideFromRoleContext(value: unknown): Side | null {
  if (typeof value !== 'string') return null;
  switch (value.trim().toLowerCase()) {
    case 'customer':
    case 'client':
      return 'client';
    case 'trade_company':
    case 'trade':
      return 'trade';
    default:
      return null;
  }
}

export function roleContextForSide(side: Side): SideRoleContext {
  return side === 'client' ? 'customer' : 'trade_company';
}

export function resolveActiveSide(profile: ActiveSideProfile | null | undefined): ActiveSide {
  const customerId = profile?.customer_id ?? null;
  const tradeCompanyId = profile?.trade_company_id ?? null;
  const availableSides: Side[] = [];
  if (customerId) availableSides.push('client');
  if (tradeCompanyId) availableSides.push('trade');

  if (customerId && tradeCompanyId) {
    const side =
      sideFromRoleContext(profile?.last_active_role) ??
      sideFromRoleContext(profile?.primary_account_type) ??
      'client';
    return {
      side,
      companyId: side === 'client' ? customerId : tradeCompanyId,
      isDual: true,
      availableSides,
    };
  }
  if (customerId) return { side: 'client', companyId: customerId, isDual: false, availableSides };
  if (tradeCompanyId) return { side: 'trade', companyId: tradeCompanyId, isDual: false, availableSides };
  return { side: null, companyId: null, isDual: false, availableSides };
}

/**
 * The roles that belong to a side: the other side's prefixed roles are left out, as
 * `sync_user_session` leaves out their permissions. Unprefixed roles (legacy OWNER, platform roles)
 * stay on both sides.
 */
export function rolesForSide<T extends { role_name: string }>(roles: readonly T[], side: Side | null): T[] {
  if (side === 'client') return roles.filter((r) => !r.role_name.startsWith('TRADE_'));
  if (side === 'trade') return roles.filter((r) => !r.role_name.startsWith('CUSTOMER_'));
  return [...roles];
}
